import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import SEO from "@/components/SEO";
import { Reveal, Eyebrow, PageBand, ComplianceNote } from "@/components/Shared";
import EnquiryForm, { BOOKING_TYPES } from "@/components/EnquiryForm";
import PhorestBooking, { OpenBookingLink } from "@/components/PhorestBooking";
import { RESPONSE_PROMISE } from "@/data/content";
import { PAGE_HEROES } from "@/data/photos";

const TYPE_SLUGS = {
  physician: "Physician consultation",
  "skin-assessment": "Skin assessment — Dr Kiran Shahid",
  hair: "Hair assessment",
  advanced: "Physician consultation",
  procedural: "Physician consultation",
  threads: "Physician consultation",
  body: "Body and wellness assessment — Dr Anthony Chaffee",
  nurse: "Skin consultation — Lizzie Stock",
  skin: "Skin consultation — Lizzie Stock",
  review: "Review — existing patient",
  unsure: "Not sure yet",
};

export const resolveBookingType = (raw) => {
  if (!raw) return "";
  const needle = (TYPE_SLUGS[raw.toLowerCase()] || raw).toLowerCase();
  return BOOKING_TYPES.find((o) => o.toLowerCase().includes(needle)) || "";
};

const Book = () => {
  const [params] = useSearchParams();
  const [status, setStatus] = useState("loading");
  const preselected = resolveBookingType(params.get("type"));
  const failed = status === "failed";
  return (
  <>
    <SEO title="Book a Consultation | Lumière Aesthetics, Cottesloe" description="Book online at Lumière Aesthetics, Cottesloe WA, or request a consultation and we'll arrange it for you. New patients begin with a consultation; fees are redeemable against treatment within 30 days." />
    <PageBand
      eyebrow="Book"
      title="Book a consultation"
      standfirst="New patients book a consultation. Existing patients may book treatments directly."
      image={PAGE_HEROES.book}
      cta={false}
    />

    <section className="bg-warm-white" data-testid="booking-online-section">
      <div className="max-w-2xl mx-auto px-8 py-24">
        <Reveal>
          <p className="text-ink/75 text-sm mb-10 text-center max-w-xl mx-auto leading-relaxed" data-testid="book-fees-line">
            Physician consultation — 45 minutes, $250 · Skin assessment with Dr Kiran Shahid — 45 minutes, $250 · Hair assessment
            with Dr Kristy Truong — 45 minutes, $250 · Body and wellness assessment with Dr Anthony Chaffee — 45 minutes, $250 ·
            Skin consultation with Lizzie Stock — 45 minutes, $100. Every fee is redeemable against treatment within 30 days.
            A deposit is taken at booking and credited to your consultation — $100 physician, $50 nurse.
            Reviews for existing patients are free.
          </p>
          <PhorestBooking status={status} onStatus={setStatus} />
          {!failed && <OpenBookingLink className="mt-4 text-center" />}
        </Reveal>
      </div>
    </section>

    <section className={failed ? "bg-warm-white -mt-24" : "bg-cream"} data-testid="booking-form-section">
      <div className="max-w-2xl mx-auto px-8 py-24">
        <Reveal>
          <Eyebrow>{failed ? "Online booking is unavailable right now" : "Prefer us to arrange it?"}</Eyebrow>
          <h2 className="text-deep-sea mt-4" data-testid="booking-form-heading">Request a consultation</h2>
          <p className="text-ink/75 mt-4 mb-10 leading-relaxed" data-testid="booking-form-intro">
            {failed
              ? `Send a request below and the practice will confirm your consultation ${RESPONSE_PROMISE}.`
              : `Tell us what you're considering and the practice will suggest a time and practitioner, ${RESPONSE_PROMISE}.`}
          </p>
          <EnquiryForm type="booking" defaults={{ consultation_type: preselected }} key={preselected} />
          <p className="text-soft-grey text-sm mt-8 leading-relaxed" data-testid="booking-confirmation-note">
            The practice confirms every request personally, {RESPONSE_PROMISE}.
          </p>
          <div className="mt-8">
            <ComplianceNote />
          </div>
        </Reveal>
      </div>
    </section>
  </>
  );
};

export default Book;
